import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import { GetInvestigationCategories } from "../../../../api/diagnosis/master/investigationCategoryAPI";
import { GetRadiologyTestById, UpdateRadiologyTest } from "../../../../api/diagnosis/master/radiologyTestAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
    name: yup.string().required("Name is required"),
    shortName: yup.string().required("Short name is required"),
    investigationCategoryId: yup
        .string()
        .required("Investigation category is required"),
    price: yup
        .number()
        .typeError("Price must be a number")
        .required("Price is required"),
    // intlPrice: yup
    //     .number()
    //     .nullable(),
    discountAllow: yup
        .string()
        .oneOf(["Yes", "No"])
        .required("Discount allow is required"),
    rateEditable: yup
        .string()
        .oneOf(["Yes", "No"])
        .required("Rate editable is required"),
    testType: yup.string().required("Test type is required"),
    reportDays: yup
        .number()
        .typeError("Report days must be a number")
        .min(0)
        .required("Report days is required"),
    active: yup
        .string()
        .oneOf(["Yes", "No"])
        .required("Active status is required"),
    remarks: yup.array().of(
        yup.object().shape({
            particulars: yup.string().required("Particulars is required"),
            type: yup.string().required("Type is required"),
            add: yup.string().nullable(),
            edit: yup.string().nullable(),
            delete: yup.string().nullable()
        })
    )
});

const EditRadiologiTest = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { id } = useParams();

    const [investigationCategories, setInvestigationCategories] = useState(
        []
    );

    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            remarks: []
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "remarks"
    });

    const fetchInvestigationCategories = async () => {
        try {
            const res = await GetInvestigationCategories();
            if (res.success) setInvestigationCategories(res.data);
            else showToast("error", res.message);
        } catch (err) {
            showToast("error", err.message);
        }
    };

    const fetchData = async () => {
        try {
            const response = await GetRadiologyTestById(id); // Make sure login function returns token

            if (response.success) {
                Object.keys(response.data).forEach(key => {
                    setValue(key, response.data[key]);
                })
            } else {
                // setError(response.message);
                showToast("error", response.message);
            }
        } catch (error) {
            // setError(error); // Handle login errors
            showToast("error", error);
        }
    };

    // Fetch existing data
    useEffect(
        () => {
            if (id) {
                fetchInvestigationCategories().then(() => {
                    fetchData();
                });
            }
        },
        [id, setValue]
    );

    const onSubmit = async data => {
        try {
            dispatch({ type: "LOADER_START" });

            const response = await UpdateRadiologyTest(id, data);
            if (response.success) {
                showToast("success", response.message);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    const formRef = useRef(null);
    useKeyboardSubmit(handleSubmit, onSubmit);
    useEnterNavigation(formRef);

    const testTypes = [
		{ value: "Same", label: "Same" },
		{ value: "DLC", label: "DLC" },
		{ value: "Widal", label: "Widal" },
		{ value: "Culture", label: "Culture" },
		{ value: "Echo", label: "Echo" },
		{ value: "Bilirubin", label: "Bilirubin" },
		{ value: "S. Protein", label: "S. Protein" },
		{ value: "A/G Ratio", label: "A/G Ratio" },
		{ value: "Lipid Profile", label: "Lipid Profile" },
		{ value: "Stool", label: "Stool" },
		{ value: "Urine", label: "Urine" },
		{ value: "Semen", label: "Semen" },
		{ value: "C.B.C.", label: "C.B.C." },
		{ value: "PT(INR)", label: "PT(INR)" },
		{ value: "Renal", label: "Renal" },
		{ value: "HbAlc", label: "HbAlc" },
		{ value: "Prothrombin", label: "Prothrombin" },
		{ value: "IRON PROFILE", label: "IRON PROFILE" },
	];

    return (
        <Fragment>
            <Breadcrumb parent="Radiology Test" title="Edit Radiology Test" />

            <div className="container-fluid">
                <div className="card">
                    <div className="card-header d-flex justify-content-between">
                        <h5>Edit Radiology Test</h5>
                        <Link
                            to={`/diagnosis/master/radiology-test/list`}
                        >
                            <button className="btn btn-primary" id="list">
                                List Radiology Tests
                            </button>
                            <UncontrolledTooltip target="list">
                                List Radiology Tests
                            </UncontrolledTooltip>
                        </Link>
                    </div>

                    <form
                        ref={formRef}
                        className="form theme-form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <div className="card-body">
                            <div className="row">

                                {/* Name */}
                                <div className="col-md-4 mt-3">
                                    <label>Name</label>
                                    <input className="form-control" {...register("name")} />
                                    <p className="text-danger">{errors.name?.message}</p>
                                </div>

                                {/* Short Name */}
                                <div className="col-md-4 mt-3">
                                    <label>Short Name</label>
                                    <input className="form-control" {...register("shortName")} />
                                    <p className="text-danger">{errors.shortName?.message}</p>
                                </div>

                                {/* Investigation Category */}
                                <div className="col-md-4 mt-3">
                                    <label>Investigation Category</label>
                                    <select
                                        className="form-control"
                                        {...register("investigationCategoryId")}
                                        placeholder="Category ID"
                                    >
                                        <option value="">Select</option>
                                        {investigationCategories.map(item => (
                                            <option key={item._id} value={item._id}>
                                                {item.name}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-danger">
                                        {errors.investigationCategoryId?.message}
                                    </p>
                                </div>

                                {/* Price */}
                                <div className="col-md-3 mt-3">
                                    <label>Price</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        {...register("price")}
                                    />
                                    <p className="text-danger">{errors.price?.message}</p>
                                </div>

                                {/* Intl Price */}
                                <div className="col-md-3 mt-3">
                                    <label>Intl Price</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        {...register("intlPrice")}
                                    />
                                    {errors.intlPrice && (
                                        <p className="text-danger">
                                            {errors.intlPrice.message}
                                        </p>
                                    )}
                                </div>

                                {/* Discount Allow */}
                                <div className="col-md-3 mt-3">
                                    <label>Discount Allow</label>
                                    <select
                                        className="form-control"
                                        {...register("discountAllow")}
                                    >
                                        <option value="">Select</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                    <p className="text-danger">
                                        {errors.discountAllow?.message}
                                    </p>
                                </div>

                                {/* Rate Editable */}
                                <div className="col-md-3 mt-3">
                                    <label>Rate Editable</label>
                                    <select
                                        className="form-control"
                                        {...register("rateEditable")}
                                    >
                                        <option value="">Select</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                    <p className="text-danger">
                                        {errors.rateEditable?.message}
                                    </p>
                                </div>

                                {/* Test Type */}
                                <div className="col-md-3 mt-3">
									<label>Test Type</label>
									<select className="form-control" {...register("testType")} >
										<option value="">Select Test Type</option>
										{testTypes.map(item => (
											<option key={item.value} value={item.value}>
												{item.label}
											</option>
										))}
									</select>
									{errors.testType &&
										<p className="text-danger">
											{errors.testType.message}
										</p>}
								</div>

                                {/* Report Days */}
                                <div className="col-md-4 mt-3">
                                    <label>Report Days</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        {...register("reportDays")}
                                    />
                                    <p className="text-danger">
                                        {errors.reportDays?.message}
                                    </p>
                                </div>

                                {/* Active */}
                                <div className="col-md-4 mt-3">
                                    <label>Active</label>
                                    <select className="form-control" {...register("active")}>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                    <p className="text-danger">{errors.active?.message}</p>
                                </div>
                            </div>
                            <hr />
                            <h5>Remarks</h5>

                            {fields.map((item, index) => (
                                <div className="row border p-3 mb-2" key={item.id}>
                                    <div className="col-md">
                                        <label>Particulars</label>
                                        <input
                                            className="form-control"
                                            {...register(`remarks.${index}.particulars`)}
                                        />
                                        <p className="text-danger">
                                            {errors.remarks?.[index]?.particulars?.message}
                                        </p>
                                    </div>

                                    <div className="col-md">
                                        <label>Type</label>
                                        <input
                                            className="form-control"
                                            {...register(`remarks.${index}.type`)}
                                        />
                                        <p className="text-danger">
                                            {errors.remarks?.[index]?.type?.message}
                                        </p>
                                    </div>

                                    <div className="col-md mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-danger"
                                            onClick={() => remove(index)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() =>
                                    append({
                                        particulars: "",
                                        type: "",
                                        add: "",
                                        edit: "",
                                        delete: ""
                                    })
                                }
                            >
                                + Add Remark
                            </button>
                        </div>

                        <div className="card-footer text-end">
                            <button className="btn btn-primary" type="submit">
                                Submit
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Fragment>
    );
};

export default EditRadiologiTest;
